import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { DatePickerFr } from '../ui/DatePickerFr';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Download, Edit, Trash2, CheckCircle, Calendar, Euro, Receipt, CalendarClock, Table, Wallet } from 'lucide-react';
import { formatDate, getPaymentMethodConfig } from './constants';

export default function AcomptesTab({
  getAllAcomptesESS, generateRecuEcheance, downloadPartialReceipt: downloadPartialReceipt,
  openEditAcompteModal, cancelAcompte, generatingRecu: generatingRecu, archiving: archiving,
  archiveRecuToGED: archiveRecuToGED,
  filterRecuStatus, setFilterRecuStatus,
  filterDateDebut, setFilterDateDebut, filterDateFin, setFilterDateFin, exportAcomptes,
  calculatedComptaStats
}) {
  return (
        <div className="space-y-6" data-testid="acomptes-content">
          {/* Header Premium */}
          <div className="bg-gradient-to-r from-rose-500 via-pink-500 to-fuchsia-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
                  <Wallet className="h-8 w-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Gestion des Acomptes ESS</h3>
                  <p className="text-rose-100 mt-1">Suivi et gestion des versements partiels</p>
                </div>
              </div>
              <Button onClick={exportAcomptes} className="bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm gap-2">
                <Download className="h-4 w-4" />
                Exporter CSV
              </Button>
            </div>
          </div>

          {/* KPIs Acomptes - Design Premium */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <Card className="relative overflow-hidden bg-gradient-to-br from-rose-500 via-pink-500 to-fuchsia-600 text-white shadow-2xl border-0 group hover:scale-[1.02] transition-transform duration-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
              <CardContent className="p-6 relative">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <p className="text-rose-100 text-xs font-medium tracking-wide uppercase">Total Acomptes</p>
                    <p className="text-4xl font-black tracking-tight">{getAllAcomptesESS.length}</p>
                  </div>
                  <div className="p-3 bg-white/20 rounded-xl">
                    <Wallet className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="relative overflow-hidden bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600 text-white shadow-2xl border-0 group hover:scale-[1.02] transition-transform duration-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
              <CardContent className="p-6 relative">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <p className="text-emerald-100 text-xs font-medium tracking-wide uppercase">Reçus</p>
                    <p className="text-4xl font-black tracking-tight">{calculatedComptaStats.acomptesRecus}</p>
                  </div>
                  <div className="p-3 bg-white/20 rounded-xl">
                    <CheckCircle className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="relative overflow-hidden bg-gradient-to-br from-blue-500 via-indigo-500 to-violet-600 text-white shadow-2xl border-0 group hover:scale-[1.02] transition-transform duration-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
              <CardContent className="p-6 relative">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <p className="text-blue-100 text-xs font-medium tracking-wide uppercase">Programmés</p>
                    <p className="text-4xl font-black tracking-tight">{calculatedComptaStats.acomptesProgrammes}</p>
                  </div>
                  <div className="p-3 bg-white/20 rounded-xl">
                    <CalendarClock className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="relative overflow-hidden bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white shadow-2xl border-0 group hover:scale-[1.02] transition-transform duration-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
              <CardContent className="p-6 relative">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <p className="text-amber-100 text-xs font-medium tracking-wide uppercase">Montant Total</p>
                    <p className="text-3xl font-black tracking-tight">
                      {calculatedComptaStats.totalAcomptes.toLocaleString('fr-FR')}
                      <span className="text-lg ml-1 font-normal text-amber-200">€</span>
                    </p>
                  </div>
                  <div className="p-3 bg-white/20 rounded-xl">
                    <Euro className="h-6 w-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filtres - Design Premium */}
          <Card className="border-0 shadow-lg bg-white">
            <CardContent className="py-5">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-2.5 border border-gray-200">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <DatePickerFr
                      value={filterDateDebut}
                      onChange={setFilterDateDebut}
                      placeholder="Date début"
                      className="w-36 border-0"
                    />
                    <span className="text-gray-400 font-bold">→</span>
                    <DatePickerFr
                      value={filterDateFin}
                      onChange={setFilterDateFin}
                      placeholder="Date fin"
                      className="w-36 border-0"
                    />
                  </div>
                  <Select value={filterRecuStatus} onValueChange={setFilterRecuStatus}>
                    <SelectTrigger className="w-[200px] bg-gray-50 border-gray-200">
                      <SelectValue placeholder="Tous les statuts" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les acomptes</SelectItem>
                      <SelectItem value="recu_echeance">Reçus uniquement</SelectItem>
                      <SelectItem value="en_attente_recu">Programmés uniquement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span className="font-medium">{getAllAcomptesESS.length} résultat(s)</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tableau des acomptes - Design Premium */}
          <Card className="border-0 shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-rose-500 via-pink-500 to-fuchsia-600 px-6 py-4">
              <h4 className="text-white font-bold text-lg flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Table className="h-5 w-5" />
                </div>
                Récapitulatif des Acomptes
              </h4>
            </div>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-rose-50 to-pink-50 border-b-2 border-rose-100">
                      <th className="px-5 py-4 text-left text-xs font-bold text-rose-800 uppercase tracking-wide">Référence</th>
                      <th className="px-5 py-4 text-left text-xs font-bold text-rose-800 uppercase tracking-wide">Souscription</th>
                      <th className="px-5 py-4 text-left text-xs font-bold text-rose-800 uppercase tracking-wide">Entreprise</th>
                      <th className="px-5 py-4 text-right text-xs font-bold text-rose-800 uppercase tracking-wide">Montant</th>
                      <th className="px-5 py-4 text-center text-xs font-bold text-rose-800 uppercase tracking-wide">Date</th>
                      <th className="px-5 py-4 text-center text-xs font-bold text-rose-800 uppercase tracking-wide">Mode</th>
                      <th className="px-5 py-4 text-center text-xs font-bold text-rose-800 uppercase tracking-wide">Statut</th>
                      <th className="px-5 py-4 text-center text-xs font-bold text-rose-800 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-rose-50">
                    {getAllAcomptesESS.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-16 text-center">
                          <div className="p-4 bg-gray-100 rounded-full w-fit mx-auto mb-4">
                            <Wallet className="h-12 w-12 text-gray-400" />
                          </div>
                          <p className="text-lg font-semibold text-gray-700">Aucun acompte enregistré</p>
                          <p className="text-gray-500 mt-2">Les acomptes apparaîtront ici une fois créés</p>
                        </td>
                      </tr>
                    ) : (
                      getAllAcomptesESS
                        .filter(a => {
                          if (filterDateDebut && new Date(a.date) < new Date(filterDateDebut)) return false;
                          if (filterDateFin && new Date(a.date) > new Date(filterDateFin)) return false;
                          if (filterRecuStatus === 'recu_echeance' && (a.is_scheduled || a.statut === 'programme')) return false;
                          if (filterRecuStatus === 'en_attente_recu' && !(a.is_scheduled || a.statut === 'programme')) return false;
                          return true;
                        })
                        .map((acompte, idx) => {
                          const isScheduled = acompte.is_scheduled || acompte.statut === 'programme';
                          const isCancelled = acompte.statut === 'annule';
                          const methodConfig = getPaymentMethodConfig(acompte.method || acompte.mode_paiement);
                          
                          return (
                            <tr key={`${acompte.docReference}-${idx}`} className={`hover:bg-rose-50/50 transition-colors ${isCancelled ? 'opacity-50 bg-red-50' : ''}`}>
                              <td className="px-5 py-4">
                                <span className="font-mono text-xs bg-rose-600 text-white px-2.5 py-1.5 rounded-lg font-semibold">
                                  {acompte.reference}
                                </span>
                              </td>
                              <td className="px-5 py-4 font-mono text-xs text-gray-600">{acompte.docReference}</td>
                              <td className="px-5 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white font-bold text-sm shadow-sm">
                                    {acompte.docName?.charAt(0)?.toUpperCase() || '?'}
                                  </div>
                                  <span className="font-semibold text-gray-900">{acompte.docName}</span>
                                </div>
                              </td>
                              <td className="px-5 py-4 text-right">
                                <span className="font-black text-lg text-gray-900">
                                  {(acompte.amount || acompte.montant || 0).toLocaleString('fr-FR')} €
                                </span>
                              </td>
                              <td className="px-5 py-4 text-center text-sm text-gray-600">{formatDate(acompte.date)}</td>
                              <td className="px-5 py-4 text-center">
                                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border ${methodConfig.color}`}>
                                  {methodConfig.shortLabel}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-center">
                                {isCancelled ? (
                                  <Badge className="bg-red-600 text-white font-bold">Annulé</Badge>
                                ) : isScheduled ? (
                                  <Badge className="bg-blue-600 text-white font-semibold">Programmé</Badge>
                                ) : (
                                  <Badge className="bg-green-600 text-white font-semibold">Reçu</Badge>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="flex justify-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0 hover:bg-amber-100"
                                    onClick={() => generateRecuEcheance(acompte.doc, acompte, acompte.acompteIndex)}
                                    title="Éditer reçu"
                                  >
                                    <Receipt className="h-4 w-4 text-amber-600" />
                                  </Button>
                                  {!isCancelled && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 p-0 hover:bg-blue-100"
                                        onClick={() => openEditAcompteModal(acompte.doc, acompte, acompte.acompteIndex)}
                                        title="Modifier"
                                      >
                                        <Edit className="h-4 w-4 text-blue-600" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 p-0 hover:bg-red-100"
                                        onClick={() => cancelAcompte(acompte.doc, acompte.acompteIndex)}
                                        title="Annuler"
                                      >
                                        <Trash2 className="h-4 w-4 text-red-600" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Résumé totaux */}
          {getAllAcomptesESS.length > 0 && (
            <Card className="shadow-lg border-0 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-3">
                <h4 className="text-white font-medium text-sm">Synthèse des acomptes</h4>
              </div>
              <CardContent className="py-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl">
                    <p className="text-xs text-amber-600 uppercase tracking-wide font-semibold mb-1">Total général</p>
                    <p className="text-3xl font-bold text-amber-700">{calculatedComptaStats.totalAcomptes.toLocaleString('fr-FR')} €</p>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl">
                    <p className="text-xs text-emerald-600 uppercase tracking-wide font-semibold mb-1">Reçus</p>
                    <p className="text-3xl font-bold text-emerald-700">
                      {getAllAcomptesESS.filter(a => !a.is_scheduled && a.statut !== 'programme' && a.statut !== 'annule').reduce((s, a) => s + (a.amount || a.montant || 0), 0).toLocaleString('fr-FR')} €
                    </p>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
                    <p className="text-xs text-blue-600 uppercase tracking-wide font-semibold mb-1">Programmés</p>
                    <p className="text-3xl font-bold text-blue-700">
                      {getAllAcomptesESS.filter(a => a.is_scheduled || a.statut === 'programme').reduce((s, a) => s + (a.amount || a.montant || 0), 0).toLocaleString('fr-FR')} €
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
  );

}
